import { computed, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { queryKeys } from '@/queries/_shared/queryKeys';
import { noCacheHeaders, noCacheQueryOptions } from '@/queries/_shared/noCache';
import http from '@/services/http';

function unwrapBody(data) {
  if (data && typeof data === 'object' && 'body' in data) return data.body;
  return data;
}

function resolvePupilId(inputPupilId) {
  const direct = Number(inputPupilId || 0);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const fromStorage = Number(localStorage.getItem('p_id') || 0);
  if (Number.isFinite(fromStorage) && fromStorage > 0) return fromStorage;

  const currentChildUin = String(localStorage.getItem('currentChildUin') || '').trim();
  if (currentChildUin) {
    try {
      const childList = JSON.parse(localStorage.getItem('childList') || '[]');
      if (Array.isArray(childList)) {
        const selected = childList.find((child) => String(child?.uin || '') === currentChildUin);
        const fromChild = Number(selected?.pupil_id || selected?._data?.pupil_id || 0);
        if (Number.isFinite(fromChild) && fromChild > 0) return fromChild;
      }
    } catch {
      // ignore storage parse errors
    }
  }

  try {
    const person = JSON.parse(localStorage.getItem('person') || '{}');
    const fromProfile = Number(person?.profile?.pupil_id || person?._data?.pupil_id || 0);
    if (Number.isFinite(fromProfile) && fromProfile > 0) return fromProfile;
  } catch {
    // ignore storage parse errors
  }

  return 0;
}

async function fetchQuarterMarks(pupilId) {
  const resolvedPupilId = resolvePupilId(pupilId);
  const response = await http.get('/marks/quarter', {
    headers: noCacheHeaders,
    params: {
      p_id: resolvedPupilId,
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchAnnualMarks(pupilId) {
  const resolvedPupilId = resolvePupilId(pupilId);
  const response = await http.get('/marks/annual', {
    headers: noCacheHeaders,
    params: {
      p_id: resolvedPupilId,
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchExamMarks(pupilId) {
  const resolvedPupilId = resolvePupilId(pupilId);
  const response = await http.get('/marks/exam', {
    headers: noCacheHeaders,
    params: {
      p_id: resolvedPupilId,
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchFinalMarks(pupilId) {
  const resolvedPupilId = resolvePupilId(pupilId);
  const response = await http.get('/marks/final', {
    headers: noCacheHeaders,
    params: {
      p_id: resolvedPupilId,
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchParallels(pupilId) {
  const resolvedPupilId = resolvePupilId(pupilId);
  const response = await http.get('/parallels', {
    headers: noCacheHeaders,
    params: {
      p_id: resolvedPupilId,
      pupil_id: resolvedPupilId
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchSubjectsByIds(ids = []) {
  const normalizedIds = ids
    .map((id) => Number(id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!normalizedIds.length) return [];

  const response = await http.get('/subjects', {
    headers: noCacheHeaders,
    params: {
      ids: normalizedIds
    }
  });
  return unwrapBody(response.data) || [];
}

export function useReportCardQuarterMarksQuery(pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.reportCard.quarter(toValue(pupilId))),
    queryFn: () => fetchQuarterMarks(toValue(pupilId)),
    enabled: computed(() => resolvePupilId(toValue(pupilId)) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export function useReportCardAnnualMarksQuery(pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.reportCard.annual(toValue(pupilId))),
    queryFn: () => fetchAnnualMarks(toValue(pupilId)),
    enabled: computed(() => resolvePupilId(toValue(pupilId)) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export function useReportCardExamMarksQuery(pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.reportCard.exam(toValue(pupilId))),
    queryFn: () => fetchExamMarks(toValue(pupilId)),
    enabled: computed(() => resolvePupilId(toValue(pupilId)) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export function useReportCardFinalMarksQuery(pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.reportCard.final(toValue(pupilId))),
    queryFn: () => fetchFinalMarks(toValue(pupilId)),
    enabled: computed(() => resolvePupilId(toValue(pupilId)) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export function useReportCardParallelsQuery(pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.reportCard.parallels(toValue(pupilId))),
    queryFn: () => fetchParallels(toValue(pupilId)),
    enabled: computed(() => resolvePupilId(toValue(pupilId)) > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export function useReportCardSubjectsByIdsQuery(ids, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.reportCard.subjectsByIds(toValue(ids) || [])),
    queryFn: () => fetchSubjectsByIds(toValue(ids) || []),
    enabled: computed(() => Array.isArray(toValue(ids)) && toValue(ids).length > 0),
    ...options,
    ...noCacheQueryOptions
  });
}

export {
  fetchAnnualMarks,
  fetchExamMarks,
  fetchFinalMarks,
  fetchParallels,
  fetchQuarterMarks,
  fetchSubjectsByIds
};
